// useStakeholderForm.ts
import { useState } from 'react';
import { FormInstance, message, UploadFile } from 'antd';
import { Stakeholder } from 'types/common';
import useDeleteFilesMutation from 'hooks/useDeleteFileMutation';

export const useStakeholderFileManagement = (
  isEdit?: boolean,
  country?: string,
) => {
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isTouched, setIsTouched] = useState(false);
  const [existingPicture, setExistingPicture] = useState<UploadFile>();
  const [profilePicture, setProfilePicture] = useState<Blob>();
  const [existingNationalIdPictures, setExistingNationalIdPictures] = useState<
    UploadFile[]
  >([]);
  const [nationalIdPictures, setNationalIdPictures] = useState<Blob[]>([]);
  const [deletedIdPictures, setDeletedIdPictures] = useState<UploadFile[]>([]);
  const [uploadedFirstPageContract, setUploadedFirstPageContract] =
    useState<UploadFile>();
  const [existingContractPictures, setExistingContractPictures] = useState<
    UploadFile[]
  >([]);
  const [firstPageContract, setFirstPageContract] = useState<Blob>();
  const [contractPictures, setContractPictures] = useState<Blob[]>([]);
  const [deletedContractPictures, setDeletedContractPictures] = useState<
    UploadFile[]
  >([]);
  const [isDraft, setIsDraft] = useState(false);

  const { mutateAsync: mutateDeleteFiles } = useDeleteFilesMutation();

  const validateFiles = () => {
    if (country === 'Vietnam') return true;
    if (!isEdit) {
      const requiredFilesMissing =
        !firstPageContract ||
        contractPictures.length === 0 ||
        nationalIdPictures.length === 0;

      if (requiredFilesMissing) {
        message.error(
          'Please add first page contract, at least one Signed Contract and one National ID picture.',
        );
        return false;
      }
      return true;
    }

    const isFirstPageDeleted =
      uploadedFirstPageContract &&
      deletedContractPictures.some(
        (file) => file.uid === uploadedFirstPageContract.uid,
      );
    const isFirstPageMissing = !firstPageContract && isFirstPageDeleted;

    const remainingContracts =
      existingContractPictures.filter(
        (file) =>
          !deletedContractPictures.some((deleted) => deleted.uid === file.uid),
      ).length + contractPictures.length;

    const remainingIds =
      existingNationalIdPictures.filter(
        (file) =>
          !deletedIdPictures.some((deleted) => deleted.uid === file.uid),
      ).length + nationalIdPictures.length;

    if (isFirstPageMissing || remainingContracts === 0 || remainingIds === 0) {
      message.error(
        'You cannot leave contract, first page contract, or national ID pictures empty.',
      );
      return false;
    }

    return true;
  };

  const initializeFiles = (data: Stakeholder, form: FormInstance) => {
    const image = data?.documentList?.[0];
    if (image && image.fileName) {
      setExistingPicture({
        uid: image?.id,
        name: image?.fileName,
        url: image?.url,
      });
    }

    const nationalIdImages = data?.nationalIdDocs;
    if (nationalIdImages?.length) {
      setExistingNationalIdPictures(
        nationalIdImages?.map((image) => ({
          uid: image?.id,
          name: image?.fileName as string,
          url: image?.url,
        })),
      );
      form.setFieldValue(['nationalIdPicture'], nationalIdImages);
    }

    const firstPageContractImage = data?.firstPageContract;
    if (firstPageContractImage) {
      setUploadedFirstPageContract({
        uid: firstPageContractImage?.id,
        name: firstPageContractImage?.fileName as string,
        url: firstPageContractImage?.url,
      });
      form.setFieldValue(['firstPageContract'], firstPageContractImage);
    }

    const contractImages = data?.contractDocs;
    if (contractImages?.length) {
      setExistingContractPictures(
        contractImages?.map((image) => ({
          uid: image?.id,
          name: image?.fileName as string,
          url: image?.url,
        })),
      );
      form.setFieldValue(['contractPicture'], contractImages);
    }
  };

  const handleDeleteFile = async () => {
    const deletedExistingFileIds = [
      ...deletedIdPictures,
      ...deletedContractPictures,
    ];

    if (!deletedExistingFileIds.length) return;

    await mutateDeleteFiles(deletedExistingFileIds.map((file) => file.uid));
  };

  return {
    isLoadingData,
    isTouched,
    existingPicture,
    profilePicture,
    existingNationalIdPictures,
    nationalIdPictures,
    deletedIdPictures,
    uploadedFirstPageContract,
    existingContractPictures,
    firstPageContract,
    contractPictures,
    deletedContractPictures,
    isDraft,
    setIsLoadingData,
    setExistingPicture,
    setExistingNationalIdPictures,
    setUploadedFirstPageContract,
    setExistingContractPictures,
    setIsTouched,
    setProfilePicture,
    setNationalIdPictures,
    setDeletedIdPictures,
    setFirstPageContract,
    setContractPictures,
    setDeletedContractPictures,
    setIsDraft,
    validateFiles,
    initializeFiles,
    handleDeleteFile,
  };
};
